import { Accordion, AccordionDetails, Grid, Typography } from '@mui/material';
import { memo, useState, useMemo, useCallback, useEffect } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { Check, ExpandMore } from '@mui/icons-material';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import FormCitizenship from './form';
import {
  CitizenFormType,
  CommonInterfaceSubscription,
} from 'subscription/view-models/common-interface-subscription';
import Close from '@mui/icons-material/Close';
import { stateValueCitizen } from 'shell/utils/state-value-citizen';
import { SubjectAnpr } from 'shell/models/act-category-list';

interface CitizenshipProps {
  isCitizenship?: boolean;
  disabledDate?: boolean;
  disabledNote?: boolean;
  isTransferNew?: boolean;
  disabled?: boolean;
  isTransferAggregation?: boolean;
  validityEqualBirthday?: boolean;
}

const CitizenshipComponent = ({
  isCitizenship = false,
  disabledDate = false,
  disabledNote = false,
  disabled = false,
  isTransferNew,
  isTransferAggregation,
  validityEqualBirthday,
}: CitizenshipProps): React.ReactElement => {
  const [open, setOpen] = useState(false);

  const formContext = useFormikContext<CommonInterfaceSubscription>();

  const errorCitizenship = useMemo(() => {
    return !!formContext.errors.citizenship;
  }, [formContext.errors.citizenship]);

  const resetForm = useCallback(() => {
    formContext.setFieldValue('citizenship', {});
  }, [formContext.setFieldValue]);

  const onChange = useCallback(
    (key, value) => {
      formContext.setFieldValue('citizenship', { ...formContext.values.citizenship, [key]: value });
    },
    [formContext.setFieldValue, formContext.values.citizenship]
  );

  useEffect(() => {
    if (!formContext.values.fatherhood?.dati3002 && !formContext.values.motherhood?.dati3002) {
      return;
    }

    const padre: SubjectAnpr | undefined = formContext.values.fatherhood?.dati3002
      ? {
          idNazionalita: formContext.values.fatherhood?.dati3002.cittadinanza
            ? formContext.values.fatherhood?.dati3002.cittadinanza[0].codiceStato
            : undefined,
          nazionalita: formContext.values.fatherhood?.dati3002.cittadinanza
            ? formContext.values.fatherhood?.dati3002.cittadinanza[0].descrizioneStato
            : undefined,
        }
      : undefined;
    const madre: SubjectAnpr | undefined = formContext.values.motherhood?.dati3002
      ? {
          idNazionalita: formContext.values.motherhood?.dati3002.cittadinanza
            ? formContext.values.motherhood?.dati3002.cittadinanza[0].codiceStato
            : undefined,
          nazionalita: formContext.values.motherhood?.dati3002.cittadinanza
            ? formContext.values.motherhood?.dati3002.cittadinanza[0].descrizioneStato
            : undefined,
        }
      : undefined;

    const state = stateValueCitizen(padre, madre);
    onChange('state', state);
  }, [formContext.values.fatherhood?.dati3002, formContext.values.motherhood?.dati3002]);

  useEffect(() => {
    if (validityEqualBirthday) {
      onChange('validity', formContext.values.generality?.birthDate);
    }
  }, [formContext.values.generality?.birthDate, validityEqualBirthday]);

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
    >
      <AccordionSummary
        aria-controls="panel-person-citizenship-content"
        id="panel-person-citizenship-header"
        expandIcon={<ExpandMore />}
      >
        {errorCitizenship ? <Close color="error" /> : <Check color="success" />}
        <Typography>Cittadinanza</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1}>
          <FormCitizenship
            onChange={onChange}
            values={formContext.values.citizenship}
            errors={formContext.errors.citizenship as FormikErrors<CitizenFormType>}
            touched={formContext.touched.citizenship as unknown as FormikTouched<CitizenFormType>}
            resetForm={resetForm}
            disabled={disabled}
            disabledState={isCitizenship}
            disabledDate={disabledDate}
            disabledNote={disabledNote}
            isTransferNew={isTransferNew || isTransferAggregation}
          />
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

CitizenshipComponent.displayName = 'Citizenship component';

const Citizenship = memo(CitizenshipComponent);

export default Citizenship;
